import { X, Zap, Users, Play, Square, Clock, UserPlus, UserMinus, Calendar, Settings, RefreshCw, Bell, Info, UserSearch, Eye, Power } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import '../styles/help-modal.css';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function HelpModal({ isOpen, onClose }: HelpModalProps) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';

  if (!isOpen) return null;

  const helpSections = [
    {
      title: t('help.sections.chargers.title'),
      icon: Zap,
      color: 'var(--color-charging)',
      items: [
        {
          icon: Play,
          title: t('help.sections.chargers.startCharging'),
          description: t('help.sections.chargers.startChargingDesc')
        },
        {
          icon: Square,
          title: t('help.sections.chargers.stopCharging'),
          description: t('help.sections.chargers.stopChargingDesc')
        },
        {
          icon: Clock,
          title: t('help.sections.chargers.extendSession'),
          description: t('help.sections.chargers.extendSessionDesc')
        },
        {
          icon: RefreshCw,
          title: t('help.sections.chargers.reduceTime'),
          description: t('help.sections.chargers.reduceTimeDesc')
        },
        {
          icon: UserSearch,
          title: t('help.sections.chargers.findUser'),
          description: t('help.sections.chargers.findUserDesc')
        },
        {
          icon: Eye,
          title: t('help.sections.chargers.viewQueueUsers'),
          description: t('help.sections.chargers.viewQueueUsersDesc')
        },
        {
          icon: Power,
          title: t('help.sections.chargers.toggleCharger'),
          description: t('help.sections.chargers.toggleChargerDesc')
        }
      ]
    },
    {
      title: t('help.sections.queue.title'),
      icon: Users,
      color: 'var(--color-primary)',
      items: [
        {
          icon: UserPlus,
          title: t('help.sections.queue.joinQueue'),
          description: t('help.sections.queue.joinQueueDesc')
        },
        {
          icon: UserMinus,
          title: t('help.sections.queue.leaveQueue'),
          description: t('help.sections.queue.leaveQueueDesc')
        },
        {
          icon: Calendar,
          title: t('help.sections.queue.postponeQueue'),
          description: t('help.sections.queue.postponeQueueDesc')
        },
        {
          icon: Users,
          title: t('help.sections.queue.viewQueue'),
          description: t('help.sections.queue.viewQueueDesc')
        }
      ]
    },
    {
      title: t('help.sections.session.title'),
      icon: Clock,
      color: 'var(--color-info)',
      items: [
        {
          icon: Zap,
          title: t('help.sections.session.viewSession'),
          description: t('help.sections.session.viewSessionDesc')
        },
        {
          icon: Clock,
          title: t('help.sections.session.sessionTimer'),
          description: t('help.sections.session.sessionTimerDesc')
        },
        {
          icon: Bell,
          title: t('help.sections.session.notifications'),
          description: t('help.sections.session.notificationsDesc')
        }
      ]
    },
    {
      title: t('help.sections.features.title'),
      icon: Settings,
      color: 'var(--color-success)',
      items: [
        {
          icon: RefreshCw,
          title: t('help.sections.features.realtime'),
          description: t('help.sections.features.realtimeDesc')
        },
        {
          icon: Bell,
          title: t('help.sections.features.telegramNotifications'),
          description: t('help.sections.features.telegramNotificationsDesc')
        },
        {
          icon: Users,
          title: t('help.sections.features.queueManagement'),
          description: t('help.sections.features.queueManagementDesc')
        },
        {
          icon: Settings,
          title: t('help.sections.features.profileSettings'),
          description: t('help.sections.features.profileSettingsDesc')
        }
      ]
    }
  ];

  return (
    <div className="modal-overlay-modern" onClick={onClose}>
      <div 
        className={`modal-modern help-modal ${isRTL ? 'rtl' : 'ltr'}`}
        dir={i18n.dir()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header-modern help-modal-header">
          <div className="help-header-content">
            <div className="help-icon-wrapper">
              <Info size={28} />
            </div>
            <div>
              <h3 className="modal-title">{t('help.title')}</h3>
              <p className="modal-subtitle">{t('help.subtitle')}</p>
            </div>
          </div>
          <button
            className="modal-close-btn"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <X size={20} />
          </button>
        </div>

        <div className="modal-content-modern help-modal-content">
          {helpSections.map((section, sectionIndex) => {
            const SectionIcon = section.icon;
            return (
              <div key={sectionIndex} className="help-section">
                <div className="help-section-header">
                  <div className="help-section-icon" style={{ color: section.color }}>
                    <SectionIcon size={24} />
                  </div>
                  <h4 className="help-section-title">{section.title}</h4>
                </div>

                <div className="help-items">
                  {section.items.map((item, itemIndex) => {
                    const ItemIcon = item.icon;
                    return (
                      <div key={itemIndex} className="help-item">
                        <div className="help-item-icon" style={{ backgroundColor: `${section.color}15` }}>
                          <ItemIcon size={18} style={{ color: section.color }} />
                        </div>
                        <div className="help-item-content">
                          <h5 className="help-item-title">{item.title}</h5>
                          <p className="help-item-description">{item.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div className="help-footer-info">
            <Info size={16} />
            <p>{t('help.footerNote')}</p>
          </div>
        </div>

        <div className="modal-actions-modern">
          <button
            className="btn-modern btn-primary-modern"
            onClick={onClose}
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
